import { useContext, useEffect, useState } from 'react';

import { capitalizeFirstLetter } from '@/utils/string';

import { getTranslatedStoreValue } from '@/components/StoresList/utils';
import type { LanguageCodeEnum } from '@/graphql/saleor/api';
import { algoliaSearchClient } from '@/integrations/algolia/client';
import { getIndexName } from '@/integrations/algolia/helpers';
import { algoliaSsrSearchClient } from '@/integrations/algolia/ssrClient';
import type {
  BoutiqueSearchResult,
  StoreHit,
} from '@/integrations/algolia/types';
import {
  fetchCMSBoutiqueDetailData,
  fetchContentfulExtraData,
  filterContentfulCollection,
} from '@/integrations/contentful';
import { ALL_COUNTRIES } from '@/lib/allCountries';
import { weekdays } from '@/lib/const';
import { paths } from '@/lib/paths';
import { messages } from '@/messages/boutique';
import { LocaleContext } from '@/providers/LocaleProvider';

interface CountryOption {
  label: string;
  value: string;
}

export const useCountryOptionsSearch = (): CountryOption[] => {
  const locale = useContext(LocaleContext);
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const index = algoliaSearchClient.initIndex(
          getIndexName('STORE', locale),
        );
        const { hits } = await index.search('', {
          filters: `type:BOUTIQUE OR type:FLAG_SHIP_BOUTIQUE`,
          attributesToRetrieve: ['country_code'],
          hitsPerPage: 2000,
        });
        const uniqueCountryCodes = Array.from(
          new Set(hits.map((hit: BoutiqueSearchResult) => hit?.country_code)),
        );
        const overlappingCountries = ALL_COUNTRIES.filter((country) =>
          uniqueCountryCodes?.includes(country.code),
        ).map(({ code, country }) => ({ label: country, value: code }));
        setCountryOptions(overlappingCountries);
      } catch (error) {
        console.error('Error fetching boutiques:', error);
      }
    };
    fetchData();
  }, [locale]);
  return countryOptions;
};
export const getGoogleMapAddress = (
  _geoloc: BoutiqueSearchResult['_geoloc'],
) => {
  return `https://www.google.com/maps/dir/?api=1&destination=${_geoloc?.lat}%2C${_geoloc?.lng}`;
};
export const getToday = (): string => {
  return new Date()
    .toLocaleDateString('en-US', { weekday: 'long' })
    .toLowerCase();
};

type FormattedHours = Array<{
  time?: string;
  val: string;
}>;

interface DaySchedule {
  openIntervals?: OpenInterval[];
}
interface ScheduleTransformToFormat {
  [day: string]: DaySchedule;
}

type OpenHoursType = {
  time?: string;
  val: string;
}[];

export const calculateNext7Days = () => {
  const weekdays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  const today = new Date();
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const dayName = weekdays[date.getDay()];
    return { date: date.toISOString().split('T')[0], dayName };
  });
};

export const updateHoursWithHolidaySchedule = (
  formattedHours: FormattedHours,
  boutiqueHit: StoreHit,
): FormattedHours => {
  const next7Days = calculateNext7Days();

  const updatedHours: FormattedHours = formattedHours.map((day) => {
    const matchingHoliday = boutiqueHit?.temporary_hours?.holiday_hours?.find(
      (holiday) =>
        holiday.date ===
        next7Days.find((d) => d.dayName.toLowerCase() === day.val.toLowerCase())
          ?.date,
    );

    if (matchingHoliday) {
      if (!matchingHoliday.open_intervals) {
        return {
          val: day.val,
          time: 'Closed',
        };
      }
      const holidayIntervals = matchingHoliday.open_intervals.map(
        (interval) => `${interval.start} - ${interval.end}`,
      );
      return {
        val: day.val,
        time: holidayIntervals.join(' / '),
      };
    }
    return day;
  });

  return updatedHours;
};
export const transformToFormat = (
  schedule: ScheduleTransformToFormat,
): { time?: string; val: string }[] => {
  const result = [];
  const todayIndex = weekdays.indexOf(getToday().toLowerCase());
  const rearrangedDays = [
    ...weekdays.slice(todayIndex),
    ...weekdays.slice(0, todayIndex),
  ];
  for (const day of rearrangedDays) {
    if (Object.prototype.hasOwnProperty.call(schedule, day)) {
      const openIntervals = schedule[day].openIntervals?.map(
        (interval: OpenInterval) => `${interval.start} - ${interval.end}`,
      );
      result.push({
        val: capitalizeFirstLetter(day),
        time: openIntervals?.join(' / '),
      });
    }
  }
  return result;
};
type TimeWithOffsetType = { time: number; weekday: string };
export const getTimeWithOffset = (utc_offset: string): TimeWithOffsetType => {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
  const offsetParts = utc_offset.split(':');
  const offsetHours = parseInt(offsetParts[0]);
  const offsetMinutes = parseInt(offsetParts[1]);
  const shopTime = new Date(
    utc + (offsetHours * 60 + offsetMinutes) * 60 * 1000,
  );
  const minutesSinceMidnight = shopTime.getHours() * 60 + shopTime.getMinutes();
  const weekday = weekdays[shopTime.getDay()].toUpperCase();
  return { time: minutesSinceMidnight, weekday };
};
interface ScheduleItem {
  time?: string;
  val?: string;
}

export type Schedule =
  | {
      [day: string]: {
        endTime: string;
        startTime: string;
      }[];
    }
  | null
  | undefined;

type TimeSlot = {
  endTime: string;
  startTime: string;
};

type MappedTime = {
  label: string;
  value: string;
};

export type OpenInterval = { end: string; start: string };
export type DateInfo = {
  date: string | undefined;
  open_intervals: OpenInterval[];
};
export type TimeInterval = { endTime: string; startTime: string };

const generateHourlyIntervals = (
  holidayData: DateInfo[],
  regularAppointments: Schedule,
  date: string | Date,
  reopenDate?: string,
): { intervals: TimeInterval[]; isDisabled: boolean } => {
  const result: TimeInterval[] = [];

  if (reopenDate) {
    const reopenDateTime = new Date(reopenDate).getTime();
    const currentDateTime = new Date(date).getTime();
    if (currentDateTime < reopenDateTime) {
      return { intervals: [], isDisabled: true };
    }
  }

  const holidayDateInfo = holidayData.find((item) => item.date === date);

  if (holidayDateInfo) {
    if (!holidayDateInfo.open_intervals) {
      return { intervals: [], isDisabled: true };
    }

    if (Array.isArray(holidayDateInfo.open_intervals)) {
      holidayDateInfo.open_intervals.forEach((interval) => {
        result.push({
          startTime: interval.start,
          endTime: interval.end,
        });
      });
      return { intervals: result, isDisabled: false };
    }
  }

  const dayOfWeek = weekdays[new Date(date).getDay()];
  const regularDayAppointments = regularAppointments?.[dayOfWeek];

  if (regularDayAppointments) {
    regularDayAppointments.forEach((interval) => {
      result.push({
        startTime: interval.startTime,
        endTime: interval.endTime,
      });
    });
    return { intervals: result, isDisabled: false };
  }

  return { intervals: [], isDisabled: true };
};

const mapTimes = (arr: TimeSlot[] | undefined): MappedTime[] => {
  return (
    arr?.map((item: TimeSlot) => {
      return {
        label: `${item.startTime} - ${item.endTime}`,
        value: item.startTime,
      };
    }) || []
  );
};

export const formatTimesOption = (
  appointments: Schedule,
  date: string | Date,
  holidayHours: any[] | undefined,
  setAvailableTimes: (times: MappedTime[]) => void,
  setNonWorkingDays: (days: string[]) => void,
  reopenDate?: string | undefined,
) => {
  const nonWorkingDays: string[] = [];
  const { intervals, isDisabled } = generateHourlyIntervals(
    holidayHours ?? [],
    appointments,
    date,
    reopenDate,
  );

  if (isDisabled) {
    setAvailableTimes([]);
    return;
  }

  (holidayHours ?? []).forEach((holiday) => {
    if (holiday.open_intervals === null) {
      nonWorkingDays.push(holiday.date);
    }
  });

  setNonWorkingDays(nonWorkingDays);
  setAvailableTimes(mapTimes(intervals));
};

export const isWithinOpenHours = (
  localTime: TimeWithOffsetType,
  openHours: OpenHoursType,
) => {
  const openingHours = openHours?.find(
    (day: ScheduleItem) =>
      day.val?.toLowerCase() === localTime.weekday.toLowerCase(),
  );
  if (!openingHours?.time) {
    return false;
  }
  const [startTime, endTime] = openingHours.time.split(' - ');
  const startHour = parseInt(startTime.split(':')[0]);
  const startMinute = parseInt(startTime.split(':')[1]);
  const endHour = parseInt(endTime.split(':')[0]);
  const endMinute = parseInt(endTime.split(':')[1]);
  const startMinutesSinceMidnight = startHour * 60 + startMinute;
  const endMinutesSinceMidnight = endHour * 60 + endMinute;
  const localMinutesSinceMidnight = localTime.time;
  return (
    localMinutesSinceMidnight >= startMinutesSinceMidnight &&
    localMinutesSinceMidnight <= endMinutesSinceMidnight
  );
};
export const countryMessages = {
  us: messages.usBtqlistHeading,
  af: messages.afBtqlistHeading,
  al: messages.alBtqlistHeading,
  dz: messages.dzBtqlistHeading,
  as: messages.asBtqlistHeading,
  ad: messages.adBtqlistHeading,
  ao: messages.aoBtqlistHeading,
  ai: messages.aiBtqlistHeading,
  ag: messages.agBtqlistHeading,
  ar: messages.arBtqlistHeading,
  am: messages.amBtqlistHeading,
  aw: messages.awBtqlistHeading,
  au: messages.auBtqlistHeading,
  at: messages.atBtqlistHeading,
  az: messages.azBtqlistHeading,
  bs: messages.bsBtqlistHeading,
  bh: messages.bhBtqlistHeading,
  bd: messages.bdBtqlistHeading,
  bb: messages.bbBtqlistHeading,
  by: messages.byBtqlistHeading,
  be: messages.beBtqlistHeading,
  bz: messages.bzBtqlistHeading,
  bj: messages.bjBtqlistHeading,
  bm: messages.bmBtqlistHeading,
  bt: messages.btBtqlistHeading,
  bo: messages.boBtqlistHeading,
  ba: messages.baBtqlistHeading,
  bw: messages.bwBtqlistHeading,
  br: messages.brBtqlistHeading,
  io: messages.ioBtqlistHeading,
  vg: messages.vgBtqlistHeading,
  bn: messages.bnBtqlistHeading,
  bg: messages.bgBtqlistHeading,
  bf: messages.bfBtqlistHeading,
  bi: messages.biBtqlistHeading,
  kh: messages.khBtqlistHeading,
  cm: messages.cmBtqlistHeading,
  ca: messages.caBtqlistHeading,
  cv: messages.cvBtqlistHeading,
  bq: messages.bqBtqlistHeading,
  ky: messages.kyBtqlistHeading,
  cf: messages.cfBtqlistHeading,
  td: messages.tdBtqlistHeading,
  cl: messages.clBtqlistHeading,
  cn: messages.cnBtqlistHeading,
  cx: messages.cxBtqlistHeading,
  cc: messages.ccBtqlistHeading,
  co: messages.coBtqlistHeading,
  km: messages.kmBtqlistHeading,
  cd: messages.cdBtqlistHeading,
  cg: messages.cgBtqlistHeading,
  ck: messages.ckBtqlistHeading,
  cr: messages.crBtqlistHeading,
  ci: messages.ciBtqlistHeading,
  hr: messages.hrBtqlistHeading,
  cu: messages.cuBtqlistHeading,
  cw: messages.cwBtqlistHeading,
  cy: messages.cyBtqlistHeading,
  cz: messages.czBtqlistHeading,
  dk: messages.dkBtqlistHeading,
  dj: messages.djBtqlistHeading,
  dm: messages.dmBtqlistHeading,
  do: messages.doBtqlistHeading,
  ec: messages.ecBtqlistHeading,
  eg: messages.egBtqlistHeading,
  sv: messages.svBtqlistHeading,
  gq: messages.gqBtqlistHeading,
  er: messages.erBtqlistHeading,
  ee: messages.eeBtqlistHeading,
  et: messages.etBtqlistHeading,
  fk: messages.fkBtqlistHeading,
  fo: messages.foBtqlistHeading,
  fj: messages.fjBtqlistHeading,
  fi: messages.fiBtqlistHeading,
  fr: messages.frBtqlistHeading,
  gf: messages.gfBtqlistHeading,
  pf: messages.pfBtqlistHeading,
  ga: messages.gaBtqlistHeading,
  gm: messages.gmBtqlistHeading,
  ge: messages.geBtqlistHeading,
  de: messages.deBtqlistHeading,
  gh: messages.ghBtqlistHeading,
  gi: messages.giBtqlistHeading,
  gr: messages.grBtqlistHeading,
  gl: messages.glBtqlistHeading,
  gd: messages.gdBtqlistHeading,
  gp: messages.gpBtqlistHeading,
  gu: messages.guBtqlistHeading,
  gt: messages.gtBtqlistHeading,
  gg: messages.ggBtqlistHeading,
  gn: messages.gnBtqlistHeading,
  gw: messages.gwBtqlistHeading,
  gy: messages.gyBtqlistHeading,
  ht: messages.htBtqlistHeading,
  hn: messages.hnBtqlistHeading,
  hk: messages.hkBtqlistHeading,
  hu: messages.huBtqlistHeading,
  is: messages.isBtqlistHeading,
  in: messages.inBtqlistHeading,
  id: messages.idBtqlistHeading,
  ir: messages.irBtqlistHeading,
  iq: messages.iqBtqlistHeading,
  ie: messages.ieBtqlistHeading,
  im: messages.imBtqlistHeading,
  il: messages.ilBtqlistHeading,
  it: messages.itBtqlistHeading,
  jm: messages.jmBtqlistHeading,
  jp: messages.jpBtqlistHeading,
  je: messages.jeBtqlistHeading,
  jo: messages.joBtqlistHeading,
  kz: messages.kzBtqlistHeading,
  ke: messages.keBtqlistHeading,
  ki: messages.kiBtqlistHeading,
  xk: messages.xkBtqlistHeading,
  kw: messages.kwBtqlistHeading,
  kg: messages.kgBtqlistHeading,
  la: messages.laBtqlistHeading,
  lv: messages.lvBtqlistHeading,
  lb: messages.lbBtqlistHeading,
  ls: messages.lsBtqlistHeading,
  lr: messages.lrBtqlistHeading,
  ly: messages.lyBtqlistHeading,
  li: messages.liBtqlistHeading,
  lt: messages.ltBtqlistHeading,
  lu: messages.luBtqlistHeading,
  mo: messages.moBtqlistHeading,
  mk: messages.mkBtqlistHeading,
  mg: messages.mgBtqlistHeading,
  mw: messages.mwBtqlistHeading,
  my: messages.myBtqlistHeading,
  mv: messages.mvBtqlistHeading,
  ml: messages.mlBtqlistHeading,
  mt: messages.mtBtqlistHeading,
  mh: messages.mhBtqlistHeading,
  mq: messages.mqBtqlistHeading,
  mr: messages.mrBtqlistHeading,
  mu: messages.muBtqlistHeading,
  yt: messages.ytBtqlistHeading,
  mx: messages.mxBtqlistHeading,
  fm: messages.fmBtqlistHeading,
  md: messages.mdBtqlistHeading,
  mc: messages.mcBtqlistHeading,
  mn: messages.mnBtqlistHeading,
  me: messages.meBtqlistHeading,
  ms: messages.msBtqlistHeading,
  ma: messages.maBtqlistHeading,
  mz: messages.mzBtqlistHeading,
  mm: messages.mmBtqlistHeading,
  na: messages.naBtqlistHeading,
  nr: messages.nrBtqlistHeading,
  np: messages.npBtqlistHeading,
  nl: messages.nlBtqlistHeading,
  nc: messages.ncBtqlistHeading,
  nz: messages.nzBtqlistHeading,
  ni: messages.niBtqlistHeading,
  ne: messages.neBtqlistHeading,
  ng: messages.ngBtqlistHeading,
  nu: messages.nuBtqlistHeading,
  nf: messages.nfBtqlistHeading,
  kp: messages.kpBtqlistHeading,
  mp: messages.mpBtqlistHeading,
  no: messages.noBtqlistHeading,
  om: messages.omBtqlistHeading,
  pk: messages.pkBtqlistHeading,
  pw: messages.pwBtqlistHeading,
  ps: messages.psBtqlistHeading,
  pa: messages.paBtqlistHeading,
  pg: messages.pgBtqlistHeading,
  py: messages.pyBtqlistHeading,
  pe: messages.peBtqlistHeading,
  ph: messages.phBtqlistHeading,
  pl: messages.plBtqlistHeading,
  pt: messages.ptBtqlistHeading,
  pr: messages.prBtqlistHeading,
  qa: messages.qaBtqlistHeading,
  re: messages.reBtqlistHeading,
  ro: messages.roBtqlistHeading,
  ru: messages.ruBtqlistHeading,
  rw: messages.rwBtqlistHeading,
  bl: messages.blBtqlistHeading,
  sh: messages.shBtqlistHeading,
  kn: messages.knBtqlistHeading,
  lc: messages.lcBtqlistHeading,
  mf: messages.mfBtqlistHeading,
  pm: messages.pmBtqlistHeading,
  vc: messages.vcBtqlistHeading,
  ws: messages.wsBtqlistHeading,
  sm: messages.smBtqlistHeading,
  st: messages.stBtqlistHeading,
  sa: messages.saBtqlistHeading,
  sn: messages.snBtqlistHeading,
  rs: messages.rsBtqlistHeading,
  sc: messages.scBtqlistHeading,
  sl: messages.slBtqlistHeading,
  sg: messages.sgBtqlistHeading,
  sx: messages.sxBtqlistHeading,
  sk: messages.skBtqlistHeading,
  si: messages.siBtqlistHeading,
  sb: messages.sbBtqlistHeading,
  so: messages.soBtqlistHeading,
  za: messages.zaBtqlistHeading,
  kr: messages.krBtqlistHeading,
  ss: messages.ssBtqlistHeading,
  es: messages.esBtqlistHeading,
  lk: messages.lkBtqlistHeading,
  sd: messages.sdBtqlistHeading,
  sr: messages.srBtqlistHeading,
  sj: messages.sjBtqlistHeading,
  sz: messages.szBtqlistHeading,
  se: messages.seBtqlistHeading,
  ch: messages.chBtqlistHeading,
  sy: messages.syBtqlistHeading,
  tw: messages.twBtqlistHeading,
  tj: messages.tjBtqlistHeading,
  tz: messages.tzBtqlistHeading,
  th: messages.thBtqlistHeading,
  tl: messages.tlBtqlistHeading,
  tg: messages.tgBtqlistHeading,
  tk: messages.tkBtqlistHeading,
  to: messages.toBtqlistHeading,
  tt: messages.ttBtqlistHeading,
  tn: messages.tnBtqlistHeading,
  tr: messages.trBtqlistHeading,
  tm: messages.tmBtqlistHeading,
  tc: messages.tcBtqlistHeading,
  tv: messages.tvBtqlistHeading,
  vi: messages.viBtqlistHeading,
  ug: messages.ugBtqlistHeading,
  ua: messages.uaBtqlistHeading,
  ae: messages.aeBtqlistHeading,
  gb: messages.gbBtqlistHeading,
  uy: messages.uyBtqlistHeading,
  uz: messages.uzBtqlistHeading,
  vu: messages.vuBtqlistHeading,
  va: messages.vaBtqlistHeading,
  ve: messages.veBtqlistHeading,
  vn: messages.vnBtqlistHeading,
  wf: messages.wfBtqlistHeading,
  eh: messages.ehBtqlistHeading,
  ye: messages.yeBtqlistHeading,
  zm: messages.zmBtqlistHeading,
  zw: messages.zwBtqlistHeading,
};
export const getCountryNameFromCode = (code: string | undefined) => {
  if (code) {
    const countryItem = ALL_COUNTRIES.find((item) => item.code === code);
    return countryItem?.country;
  } else {
    return '';
  }
};

// only for ssr, client please not use this function
export const getBoutiqueDataWithTrackingKey = async (
  isFromDetailDirect: boolean,
  storeId: string,
  locale: string,
): Promise<
  { countryCode: string | undefined; slug: string | undefined } | 'no hit'
> => {
  const index = algoliaSsrSearchClient.initIndex(getIndexName('STORE', locale));
  const filters = isFromDetailDirect
    ? `type:BOUTIQUE OR type:FLAG_SHIP_BOUTIQUE`
    : `(type:BOUTIQUE OR type:FLAG_SHIP_BOUTIQUE OR type:OFFICIAL_RETAILER) AND offers_appointment:true`;
  const { hits } = await index.search<BoutiqueSearchResult>('', {
    filters: filters,
    attributesToRetrieve: ['objectID', 'country_code', 'slug', 'type'],
    facetFilters: [`store_id:${storeId}`],
  });
  if (hits.length) {
    return {
      slug: hits[0]?.slug,
      countryCode: hits[0]?.country_code,
    };
  }
  return 'no hit';
};
export const generateLanguageFields = (
  baseField: string,
  languages = [
    '', // 默认语言
    'de',
    'en',
    'es',
    'fr',
    'it',
    'ja',
    'ko',
    'pt',
    'zh_Hans',
    'zh_Hant',
  ],
) => languages.map((lang) => (lang ? `${baseField}_${lang}` : baseField));

/**
 * Get store information by slug. For SSR only.
 * @param slug Store slug
 * @param locale
 * @param languageSlug Language slug in URL
 */
export const getStoreIdWithSlug = async (
  slug: string,
  locale: string,
  languageSlug: string,
): Promise<
  { name: string | undefined; storeId: string | undefined } | 'no hit'
> => {
  const index = algoliaSsrSearchClient.initIndex(getIndexName('STORE', locale));
  const nameFields = generateLanguageFields('name');
  const { hits } = await index.search<BoutiqueSearchResult>('', {
    attributesToRetrieve: ['store_id', ...nameFields],
    facetFilters: [`slug:${slug}`],
  });

  const translatedName = hits[0]
    ? getTranslatedStoreValue(hits[0] as StoreHit, 'name', languageSlug)
    : '';

  if (hits.length) {
    return {
      storeId: hits[0]?.store_id,
      name: translatedName,
    };
  }
  return 'no hit';
};
// only for ssr, client please not use this function
export const getStoreOfferringAppointmentWithSlug = async (
  slug: string,
  locale: string,
  languageSlug: string,
): Promise<
  { name: string | undefined; storeId: string | undefined } | 'no hit'
> => {
  const index = algoliaSsrSearchClient.initIndex(getIndexName('STORE', locale));
  const nameFields = generateLanguageFields('name');
  const { hits } = await index.search<BoutiqueSearchResult>('', {
    filters: `(type:BOUTIQUE OR type:FLAG_SHIP_BOUTIQUE OR type:OFFICIAL_RETAILER) AND offers_appointment:true`,
    attributesToRetrieve: ['store_id', ...nameFields],
    facetFilters: [`slug:${slug}`],
  });

  const translatedName = hits[0]
    ? getTranslatedStoreValue(hits[0] as StoreHit, 'name', languageSlug)
    : '';

  if (hits.length) {
    return {
      storeId: hits[0]?.store_id,
      name: translatedName,
    };
  }
  return 'no hit';
};

export enum BoutiqueDetailCMSTypeEnum {
  Appointment = 'Appointment',
  Detail = 'Detail',
}
export const fetchCMSBoutiqueDetailContentWithExtraData = async (
  storeName: string,
  countryCode: string,
  languageCode: LanguageCodeEnum,
  channel: string,
  locale?: string,
  type?: BoutiqueDetailCMSTypeEnum,
) => {
  let cmsBoutiqueDetailData = await fetchCMSBoutiqueDetailData(
    false,
    countryCode,
    locale,
    type,
    storeName,
  );
  const total =
    cmsBoutiqueDetailData.PageBoutiqueDetailData.data
      .pageBoutiqueDetailCollection?.total;
  let items =
    cmsBoutiqueDetailData?.PageBoutiqueDetailData?.data
      ?.pageBoutiqueDetailCollection?.items[0]?.contentCollection?.items;
  if (total === 0) {
    cmsBoutiqueDetailData = await fetchCMSBoutiqueDetailData(
      true,
      countryCode,
      locale,
      type,
    );
    items =
      cmsBoutiqueDetailData?.PageBoutiqueDetailData?.data
        ?.pageBoutiqueDetailCollection?.items[0]?.contentCollection?.items;
  }
  const cmsData = filterContentfulCollection(items);
  const cmsExtraData = await fetchContentfulExtraData(
    cmsData,
    languageCode,
    channel,
  );

  return {
    items: cmsData,
    cmsExtraData,
    seoConfigBuilder: cmsBoutiqueDetailData.seoConfigBuilder,
  };
};

export const navToAppointmentPath = (
  countryCode: string,
  boutiqueSlug: string,
  locale?: string,
) => {
  const localeBeforePath = locale ?? 'us-en';
  const path = paths.stores.appointment.asPath({
    country: countryCode,
    slug: boutiqueSlug,
  });
  return `/${localeBeforePath}${path}`;
};
export const navToBoutiquePath = (
  countryCode: string,
  boutiqueSlug: string,
  locale?: string,
) => {
  const localeBeforePath = locale ?? 'us-en';
  const path = paths.stores.breitlingBoutiques.asPath({
    country: countryCode,
    slug: boutiqueSlug,
  });
  return `/${localeBeforePath}${path}`;
};
export const reorderStores = (stores: StoreHit[]): StoreHit[] => {
  return stores.sort((a: StoreHit, b: StoreHit) => {
    const typeOrder: { [key: string]: number } = {
      FLAG_SHIP_BOUTIQUE: 1,
      BOUTIQUE: 2,
      OFFICIAL_RETAILER: 3,
    };
    const operatingTypeOrder: { [key: string]: number } = {
      Internal: 0,
      External: 1,
    };
    if (a.type && b.type && typeOrder[a.type] !== typeOrder[b.type]) {
      return typeOrder[a.type] - typeOrder[b.type];
    }

    if (a.type === 'FLAG_SHIP_BOUTIQUE' || a.type === 'BOUTIQUE') {
      if (
        a.operating_type &&
        b.operating_type &&
        operatingTypeOrder[a.operating_type] !==
          operatingTypeOrder[b.operating_type]
      ) {
        return (
          operatingTypeOrder[a.operating_type] -
          operatingTypeOrder[b.operating_type]
        );
      }
    }
    return 0;
  });
};
